import React, { useEffect, useRef, useState } from "react";
import "./individualMedical.scss";
import "./Limits/LimitsStyle.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  getIndividualPlanOptions,
  getIndividualPlans,
  getPolicyDocumentsEnglishMedical,
  individualChoosedPlan,
  individualSelectedPlan,
} from "../../Redux/IndividualMedicalRedux";
import { useTranslation } from "react-i18next";
import { updateLeadsMedical } from "../../Redux/LogsRedux/Medical/MedicalLeadsRedux";
import CustomModal from "../Common/CustomModal/CustomModal";

function IndividualMedical({ planDetails: planDetailsProp }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [planOptions, setPlanOptions] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [fileName, setFileName] = useState();
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [documentCache, setDocumentCache] = useState({});
  const documentFetchRef = useRef(new Map());
  const planDetails = planDetailsProp || location.state?.planDetails || {};

  const individualPlans = planDetails?.individualPlans;
  const { t,i18n} = useTranslation();

  useEffect(() => {
    dispatch(getIndividualPlanOptions()).then((result) => {
      if (result.success) {
        setPlanOptions(result.data);
      }
    });
  }, []);

  const [filteredPremiums, setFilteredPremiums] = useState({
    gold: null,
    silver: null,
    green: null,
  });

  useEffect(() => {
    const filterPlan = (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return null;
      // Find min annual_limit in the whole array
      const minAnnualLimit = Math.min(
        ...arr.map((p) => Number(p["annual-limit"])),
      );
      // Find max co_pays in the whole array
      const maxCoPays = Math.max(...arr.map((p) => Number(p["co-pays"])));
      // Find max out_patient in the whole array
      const maxOutPatient = Math.max(
        ...arr.map((p) => Number(p["out-patient"])),
      );
      // Find the object that matches all three
      const match = arr.find(
        (p) =>
          Number(p["annual-limit"]) === minAnnualLimit &&
          Number(p["co-pays"]) === maxCoPays &&
          Number(p["out-patient"]) === maxOutPatient,
      );
      return match || null;
    };
    setFilteredPremiums({
      gold: filterPlan(individualPlans?.gold),
      silver: filterPlan(individualPlans?.silver),
      green: filterPlan(individualPlans?.green),
    });
  }, [individualPlans]);

  const getAnnualLimitRange = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0)
      return { min: null, max: null };
    const limits = arr.map((p) => Number(p["annual-limit"]));
    return {
      min: Math.min(...limits),
      max: Math.max(...limits),
    };
  };

  const greenRange = getAnnualLimitRange(individualPlans?.green);
  const silverRange = getAnnualLimitRange(individualPlans?.silver);
  const goldRange = getAnnualLimitRange(individualPlans?.gold);

  const [planSelections, setPlanSelections] = useState({});
  const [planAmounts, setPlanAmounts] = useState({
    green: "00.00",
    silver: "00.00",
    gold: "00.00",
  });
  const [showConfirm, setShowConfirm] = useState(false);
  const [planValidationError, setPlanValidationError] = useState("");
  const formatAmount = (value) => {
    if (value == null || Number.isNaN(Number(value))) return "00.00";
    return Number(value).toFixed(3);
  };
  const getPlanKeyFromName = (name) =>
    name ? name.toLowerCase().split(" ")[0] : "green";
  const getSliderDisplayValues = (values) => {
    if (!Array.isArray(values) || values.length === 0) return [];
    if (values.length > 2) return values;
    return values.length === 1 ? [values[0]] : [values[0], values[1]];
  };

  const extractPlanOptions = (planArray) => {
    if (!Array.isArray(planArray) || planArray.length === 0) {
      return { annualLimits: [], opDetectable: [], coPays: [] };
    }

    const annualLimits = [
      ...new Set(planArray.map((p) => Number(p["annual-limit"]))),
    ].sort((a, b) => a - b);

    const opDetectable = [
      ...new Set(planArray.map((p) => Number(p["out-patient"]))),
    ].sort((a, b) => a - b);

    const coPaysRaw = [...new Set(planArray.map((p) => p["co-pays"]))];
    const coPays = coPaysRaw
      .map((v) => {
        const raw = String(v).trim();
        const isNil = raw.toLowerCase() === "nil" || raw === "0";
        const normalizedValue = isNil ? "0" : raw.replace(/%/g, "");
        const displayLabel = isNil ? "Nil" : raw;
        return {
          label: displayLabel,
          value: normalizedValue,
        };
      })
      .sort((a, b) => Number(a.value) - Number(b.value));

    return { annualLimits, opDetectable, coPays };
  };

  const greenOptions = extractPlanOptions(individualPlans?.green);
  const silverOptions = extractPlanOptions(individualPlans?.silver);
  const goldOptions = extractPlanOptions(individualPlans?.gold);

  useEffect(() => {
    setPlanAmounts((prev) => ({
      green:
        prev.green === "00.00"
          ? formatAmount(filteredPremiums.green?.premium)
          : prev.green,
      silver:
        prev.silver === "00.00"
          ? formatAmount(filteredPremiums.silver?.premium)
          : prev.silver,
      gold:
        prev.gold === "00.00" ? formatAmount(filteredPremiums.gold?.premium) : prev.gold,
    }));
  }, [filteredPremiums]);

  const planDetail = [
    {
      planAmount: filteredPremiums.green?.premium,
      planName: "Green Plan",
      planDesc: "...",
      descKey: "plans.greenPlanDesc",
      planClass: "greenPlan",
      min: greenRange.min,
      max: greenRange.max,
      link: "network-list",
      filename: "Network List",
      planKey: "green",
      ...greenOptions,
    },
    {
      planAmount: filteredPremiums.silver?.premium,
      planName: "Silver Plan",
      planDesc: "...",
      descKey: "plans.silverPlanDesc",
      planClass: "silverPlan",
      min: silverRange.min,
      max: silverRange.max,
      link: "network-list",
      filename: "Network List",
      planKey: "silver",
      ...silverOptions,
    },
    {
      planAmount: filteredPremiums.gold?.premium,
      planName: "Gold Plan",
      planDesc: "...",
      descKey: "plans.goldPlanDesc",
      planClass: "goldPlan",
      min: silverRange.min,
      max: silverRange.max,
      link: "network-list",
      filename: "Network List",
      planKey: "gold",
      ...goldOptions,
    },
  ];

  const handlePlanSelect = async (plan) => {
    if (selectedPlan?.planName === plan.planName) return;
    if (planValidationError) {
      setPlanValidationError("");
    }
    setSelectedPlan(plan);
    await dispatch(individualSelectedPlan(plan));
  };

  useEffect(() => {
    if (!selectedPlan) return;
    const currentPlan = planDetail.find(
      (p) => p.planName === selectedPlan.planName,
    );
    if (!currentPlan) return;

    const filteredPlan = filteredPremiums[currentPlan.planKey];
    if (filteredPlan) {
      setPlanSelections((prev) => ({
        ...prev,
        [currentPlan.planKey]: {
          annualLimit: Number(filteredPlan["annual-limit"]),
          opDetectable: Number(filteredPlan["out-patient"]),
          coPay: String(filteredPlan["co-pays"]),
        },
      }));
    } else {
      setPlanSelections((prev) => ({
        ...prev,
        [currentPlan.planKey]: {
          annualLimit:
            prev[currentPlan.planKey]?.annualLimit ??
            (currentPlan.annualLimits && currentPlan.annualLimits.length > 0
              ? currentPlan.annualLimits[0]
              : null),
          opDetectable:
            prev[currentPlan.planKey]?.opDetectable ??
            (currentPlan.opDetectable && currentPlan.opDetectable.length > 0
              ? currentPlan.opDetectable[0]
              : null),
          coPay:
            prev[currentPlan.planKey]?.coPay ??
            (currentPlan.coPays && currentPlan.coPays.length > 0
              ? currentPlan.coPays[0].value
              : null),
        },
      }));
    }
  }, [selectedPlan, filteredPremiums]);

  useEffect(() => {
    if (!selectedPlan) return;
    const planKey = getPlanKeyFromName(selectedPlan.planName);
    const plansArr = individualPlans?.[planKey] || [];

    const normalizeCoPay = (v) => {
      if (v == null) return "";
      const s = String(v).trim();
      if (s.toLowerCase() === "nil" || s === "0") return "Nil";
      return s.replace(/%/g, "");
    };

    if (!plansArr || plansArr.length === 0) {
      return;
    }

    const selection = planSelections[planKey] || {};
    const targetAnnual =
      selection.annualLimit != null ? String(selection.annualLimit) : null;
    const targetOp =
      selection.opDetectable != null ? String(selection.opDetectable) : null;
    const targetCo =
      selection.coPay != null ? normalizeCoPay(selection.coPay) : null;

    const match = plansArr.find((p) => {
      const pAnnual = String(p["annual-limit"]);
      const pOp = String(p["out-patient"]);
      const pCo = normalizeCoPay(p["co-pays"]);
      return (
        (targetAnnual == null || pAnnual === targetAnnual) &&
        (targetOp == null || pOp === targetOp) &&
        (targetCo == null || pCo === targetCo)
      );
    });

    setPlanAmounts((prev) => ({
      ...prev,
      [planKey]: formatAmount(match?.premium ?? selectedPlan?.planAmount),
    }));
  }, [planSelections, individualPlans, selectedPlan]);

  const buildSelectedLimitData = () => ({
    planName: selectedPlan.planName,
    selectedPlanAmount:
      planAmounts[getPlanKeyFromName(selectedPlan.planName)] || "00.00",
    ...(function () {
      const planKey = getPlanKeyFromName(selectedPlan.planName);
      const sel = planSelections[planKey] || {};
      return {
        annualLimit: sel.annualLimit,
        opDetectable: sel.opDetectable,
        coPay: sel.coPay,
      };
    })(),
  });

  const handleSubmit = async () => {
    if (!selectedPlan) {
      setPlanValidationError("Please select the plan");
      return;
    }
    handleConfirm();
    setPlanValidationError("");
    // setShowConfirm(true);
  };

  const handleConfirm = async () => {
    if (!selectedPlan) return;
    const data = buildSelectedLimitData();
    const updatedData = {
      ...planDetails,
      selectedPlan: selectedPlan,
      selectedLimit: data,
      selectedPlanOptions: planOptions,
    };

    await dispatch(individualChoosedPlan(data));
    setShowConfirm(false);
    const payload = {
      transId: localStorage.getItem("MEDICAL_LEAD_ID"),
      selectedPlan: selectedPlan,
      selectedPlanLimits: data,
    };

    dispatch(updateLeadsMedical(payload)).then((response) => {
      if (response.success) {
        navigate("/personal-information", {
          state: { planDetails: updatedData },
        });
      }
    });
  };

  const downloadDetails = [
    {
      label: "view table of benefits",
      link: "table-of-benefits",
      filename: "Table Of Benefits",
    },
    {
      label: "view exclusion list",
      link: "policy-exclusion",
      filename: "Exclusion List",
    },
    {
      label: "view policy wording",
      link: "policy-wording",
      filename: "Policy Wording",
    },
    {
      label: "view covid-19 terms and conditions",
      link: "covid-19-terms-and-conditions",
      filename: "Covid-19 Terms And Condition",
    },
  ];

  const getDocumentKey = (policy) => {
    let documentName = policy?.link || policy;

    if (["network-list", "table-of-benefits"].includes(documentName)) {
      const planKey =
        policy?.planKey ||
        policy?.planName?.toLowerCase().split(" ")[0] ||
        "green";

      documentName = `${documentName}-${planKey}`;
    }

    return documentName;
  };

  const getProxyUrl = (url) => {
    if (!url) return null;
    return `${process.env.REACT_APP_API_URL}/proxy-pdf?url=${encodeURIComponent(url)}`;
  };

  const fetchDocumentByKey = async (documentKey) => {
    const cachedEntry = documentCache[documentKey];
    if (cachedEntry?.proxyUrl) {
      return cachedEntry;
    }

    const inFlightRequest = documentFetchRef.current.get(documentKey);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    const requestPromise = dispatch(getPolicyDocumentsEnglishMedical(documentKey))
      .then((res) => {
        const pdfUrl = res?.data || null;
        const proxyUrl = getProxyUrl(pdfUrl);

        const cachedValue = {
          url: pdfUrl,
          proxyUrl,
          status: pdfUrl ? "loaded" : "error",
        };

        setDocumentCache((prev) => ({
          ...prev,
          [documentKey]: cachedValue,
        }));

        return cachedValue;
      })
      .catch((error) => {
        console.error("PDF preview prefetch failed:", error);
        const cachedValue = {
          url: null,
          proxyUrl: null,
          status: "error",
        };

        setDocumentCache((prev) => ({
          ...prev,
          [documentKey]: cachedValue,
        }));
        return cachedValue;
      })
      .finally(() => {
        documentFetchRef.current.delete(documentKey);
      });

    documentFetchRef.current.set(documentKey, requestPromise);
    return requestPromise;
  };

  useEffect(() => {
    const preloadDocumentKeys = [
      "policy-exclusion",
      "policy-wording",
      "covid-19-terms-and-conditions",
      "network-list-green",
      "network-list-silver",
      "network-list-gold",
      "table-of-benefits-green",
      "table-of-benefits-silver",
      "table-of-benefits-gold",
    ];

    preloadDocumentKeys.forEach((documentKey) => {
      fetchDocumentByKey(documentKey);
    });
  }, []);

  const resetPreviewState = () => {
    setSelectedPdf(null);
    setPreviewUrl(null);
  };

  const handlePreview = async (policy, planKey) => {
    resetPreviewState();
    setFileName(policy.filename);

    try {
      const documentKey = getDocumentKey(policy);
      const cachedDocument = await fetchDocumentByKey(documentKey);

      if (!cachedDocument?.url || !cachedDocument?.proxyUrl) {
        throw new Error("No document data received");
      }

      setSelectedPdf(cachedDocument.url);
      setPreviewUrl(cachedDocument.proxyUrl);
      setIsSummaryOpen(true);
    } catch (err) {
      console.error("PDF preview failed:", err);
    }
  };

  return (
    <>
      <CustomModal
        isOpen={isSummaryOpen}
        onClose={() => {
          setIsSummaryOpen(false);
          resetPreviewState();
        }}
        title={fileName}
        download={true}
        pdfUrl={selectedPdf}
      >
        <div
          className="modal-body"
          style={{ overflow: "hidden", padding: 0, height: "70vh" }}
        >
          {previewUrl ? (
            <iframe
              key={previewUrl}
              title="View Document"
              src={`${previewUrl}#page=1&toolbar=0`}
              width="100%"
              height="95%"
              style={{ border: "none" }}
            />
          ) : null}
        </div>
      </CustomModal>
      <div className="row">
        <div className="col-md-12 p-3">
          {/* <h3 className="title pt-2 mb-2">{t(planDetails?.coverPlan)}</h3> */}
          <h5 className="title subHeader fw-bold py-1 mb-0">
            {t("choose your plan")}
          </h5>
        </div>
      </div>
      <div className="row plan-grid gx-5 d-flex justify-content-center">
        {planDetail &&
          planDetail.map((plan) => (
            <div className="col-lg-3 col-md-4"  key={plan.planName}>
              <div
                className={`plan-card-ind p-4 ${plan.planClass} ${selectedPlan?.planName === plan.planName ? "active-plan" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => handlePlanSelect(plan)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handlePlanSelect(plan);
                  }
                }}
              >
                <div className="plan-info p-1">
                  <p className="plan-name">{t(plan?.planName)}</p>

                  <div
                    className="row mt-4 mb-2 plan-customizer"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <div className="planLimitwrapper col-12 mb-3">
                      <h4 className="plan-limit">{t("Annual Limit")}</h4>
                      {plan.annualLimits && plan.annualLimits.length > 0 && (
                        <>
                          <input
                            type="range"
                            className="form-range"
                            min={0}
                            max={plan.annualLimits.length - 1}
                            step={1}
                            value={Math.max(
                              0,
                              plan.annualLimits.indexOf(
                                (planSelections[plan.planKey] || {})
                                  .annualLimit,
                              ) > -1
                                ? plan.annualLimits.indexOf(
                                    (planSelections[plan.planKey] || {})
                                      .annualLimit,
                                  )
                                : 0,
                            )}
                            list={`annual-limit-marks-${plan.planKey}`}
                            onChange={(e) => {
                              if (selectedPlan?.planName === plan.planName) {
                                setPlanSelections((prev) => ({
                                  ...prev,
                                  [plan.planKey]: {
                                    ...(prev[plan.planKey] || {}),
                                    annualLimit:
                                      plan.annualLimits[Number(e.target.value)],
                                  },
                                }));
                              }
                            }}
                          />
                          <div
                            className={`slider-ticks ${
                              plan.annualLimits.length === 1 ? "single" : ""
                            }`}
                            aria-hidden="true"
                          >
                            {plan.annualLimits.map((_, index) => (
                              <span
                                className="slider-tick"
                                key={`annual-tick-${index}`}
                              />
                            ))}
                          </div>
                          <datalist id={`annual-limit-marks-${plan.planKey}`}>
                            {plan.annualLimits.map((value, index) => (
                              <option
                                key={`annual-mark-${value}-${index}`}
                                value={index}
                                label={`${value.toLocaleString()} OMR`}
                              />
                            ))}
                          </datalist>
                          <div className="slider-labels">
                            {getSliderDisplayValues(plan.annualLimits).map(
                              (value, index) => (
                                <span
                                  className="plan-radio"
                                  key={`annual-label-${value}-${index}`}
                                >
                                  {value.toLocaleString()} OMR
                                </span>
                              ),
                            )}
                          </div>
                          {plan.annualLimits.length === 2 && (
                            <span style={{ display: "block", height: "18px" }}>
                              &nbsp;
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    <div className="planLimitwrapper col-12 mb-3">
                      <h4 className="plan-limit">{t("Op deductible")}</h4>
                      {plan.opDetectable && plan.opDetectable.length > 0 && (
                        <>
                          <input
                            type="range"
                            className="form-range"
                            min={0}
                            max={plan.opDetectable.length - 1}
                            step={1}
                            value={Math.max(
                              0,
                              plan.opDetectable.indexOf(
                                (planSelections[plan.planKey] || {})
                                  .opDetectable,
                              ) > -1
                                ? plan.opDetectable.indexOf(
                                    (planSelections[plan.planKey] || {})
                                      .opDetectable,
                                  )
                                : 0,
                            )}
                            onChange={(e) => {
                              if (selectedPlan?.planName === plan.planName) {
                                setPlanSelections((prev) => ({
                                  ...prev,
                                  [plan.planKey]: {
                                    ...(prev[plan.planKey] || {}),
                                    opDetectable:
                                      plan.opDetectable[Number(e.target.value)],
                                  },
                                }));
                              }
                            }}
                          />
                          <div
                            className={`slider-ticks ${
                              plan.opDetectable.length === 1 ? "single" : ""
                            }`}
                            aria-hidden="true"
                          >
                            {plan.opDetectable.map((_, index) => (
                              <span
                                className="slider-tick"
                                key={`op-tick-${index}`}
                              />
                            ))}
                          </div>
                          <div className="slider-labels">
                            {getSliderDisplayValues(plan.opDetectable).map(
                              (value, index) => (
                                <span
                                  className="plan-radio"
                                  key={`op-label-${value}-${index}`}
                                >
                                  {value.toLocaleString()} OMR
                                </span>
                              ),
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    <div className="planLimitwrapper col-12">
                      <h4 className="plan-limit">{t("PM Co Pay")}</h4>
                      {plan.coPays && plan.coPays.length > 0 && (
                        <>
                          <input
                            type="range"
                            className="form-range"
                            min={0}
                            max={plan.coPays.length - 1}
                            step={1}
                            value={Math.max(
                              0,
                              plan.coPays.findIndex(
                                (opt) =>
                                  opt.value ===
                                  (planSelections[plan.planKey] || {}).coPay,
                              ) > -1
                                ? plan.coPays.findIndex(
                                    (opt) =>
                                      opt.value ===
                                      (planSelections[plan.planKey] || {})
                                        .coPay,
                                  )
                                : 0,
                            )}
                            onChange={(e) => {
                              if (selectedPlan?.planName === plan.planName) {
                                const next =
                                  plan.coPays[Number(e.target.value)]?.value;
                                setPlanSelections((prev) => ({
                                  ...prev,
                                  [plan.planKey]: {
                                    ...(prev[plan.planKey] || {}),
                                    coPay: next,
                                  },
                                }));
                              }
                            }}
                          />
                          <div
                            className={`slider-ticks ${
                              plan.coPays.length === 1 ? "single" : ""
                            }`}
                            aria-hidden="true"
                          >
                            {plan.coPays.map((_, index) => (
                              <span
                                className="slider-tick"
                                key={`copay-tick-${index}`}
                              />
                            ))}
                          </div>
                          <div className="slider-labels">
                            {getSliderDisplayValues(plan.coPays).map(
                              (option, index) => (
                                <span
                                  className="plan-radio"
                                  key={`copay-label-${option?.value}-${index}`}
                                >
                                  {option?.label}
                                </span>
                              ),
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <hr />
                  {/* <p className="plan-label">{t("plan starting from")}</p> */}
                  <p className="p-0 m-0 lh-1">{t("Premium")}</p>
                  <p
                    className={`plan-price ${
                      selectedPlan?.planName === plan.planName
                        ? "selected-price"
                        : ""
                    }`}
                  >
                    <span className="price-amount">
                      {planAmounts[plan.planKey] ??
                        formatAmount(plan?.planAmount)}
                    </span>
                    <span className="price-currency"> OMR</span>
                  </p>

                  <p className="plan-desc">
                    {" "}
                    {t(plan.descKey, {
                      min: plan.min?.toLocaleString(),
                      max: plan.max?.toLocaleString(),
                    })}
                  </p>
                  <hr />
                 <div className={`plan-download ${i18n.language === "ar" ? "text-end" : "text-start"} mb-2`}>
                    <i className="bi bi-file-earmark-text me-1"></i>
                    <a
                      href={plan?.link}
                      className="download-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePreview(plan);
                      }}
                    >
                      {t("view network list")}
                    </a>
                  </div>
                  <div className={i18n.language === "ar" ? "text-end" : "text-start"}>
                    {downloadDetails &&
                      downloadDetails.map((download) => (
                        <p className="plan-download mb-2" key={download.label}>
                          <i className="bi bi-file-earmark-text me-1"></i>
                          {download.link ? (
                            <a
                              href={download.link}
                              className="download-link"
                              onClick={(e) => {
                                e.preventDefault();
                                handlePreview({
                                  ...download,
                                  planKey: plan.planKey,
                                });
                              }}
                            >
                              {t(download.label)}
                            </a>
                          ) : (
                            <a href="#" className="download-link">
                              {download.label}
                            </a>
                          )}
                        </p>
                      ))}
                  </div>
                </div>
                <div className="planSelect">
                  <a
                    className={`round ${
                      selectedPlan?.planName === plan.planName ? "active" : ""
                    }`}
                    onClick={() => handlePlanSelect(plan)}
                  >
                    <i
                      className={`bi bi-check-lg plan-select-icon ${
                        selectedPlan?.planName === plan.planName
                          ? "active-icon"
                          : ""
                      }`}
                    ></i>
                  </a>
                </div>
              </div>
            </div>
          ))}
           {planValidationError && !selectedPlan && (
        <div className="row">
          <div className="col-md-12 text-center">
            <p className="text-danger m-0">{t(planValidationError)}</p>
          </div>
        </div>
      )}
      </div>
      <div className="row justify-content-center pb-4">
        <div className="col-lg-4 col-md-4"></div>
        <div className="col-lg-4 col-md-4"></div>
          <div className={`col-lg-4 col-md-4 ${i18n.language === "ar" ? "text-start" : "text-end"} mb-2`}>
          <button
            type="button"
            onClick={handleSubmit}
            className="btn btn-primary"
          >
            {t("Next")}
          </button>
        </div>
        </div>
      {showConfirm && (
        <>
          <div
            className="modal fade show"
            style={{ display: "block" }}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header border-0">
                  <h5 className="title">{t("Confirm Plan")}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowConfirm(false)}
                  ></button>
                </div>
                <div className="modal-body pt-0 pb-0">
                  <div className="plan-card-plan mb-2 border p-3">
                    <div className="plan-info mb-2">
                      <p className="plan-name mb-2">
                        {t(selectedPlan?.planName)}
                      </p>
                      {(() => {
                        const planKey = selectedPlan?.planName
                          ? selectedPlan.planName.toLowerCase().split(" ")[0]
                          : "green";
                        const sel = planSelections[planKey] || {};
                        return (
                          <>
                            <p className="plan-desc mb-1">
                              {t("Annual Limit")} :{" "}
                              {sel.annualLimit?.toLocaleString()} OMR
                            </p>
                            <p className="plan-desc mb-1">
                              {t("Op deductible")}:{" "}
                              {sel.opDetectable?.toLocaleString()} OMR
                            </p>
                            <p className="plan-desc mb-1">
                              {t("PM Co Pay")}:{" "}
                              {sel.coPay === "0" ? "Nil" : sel.coPay}
                            </p>
                          </>
                        );
                      })()}
                    </div>
                    <div className="plan-action">
                      <p className="plan-price">
                        <span className="price-amount">
                          {selectedPlan
                            ? `${
                                planAmounts[
                                  getPlanKeyFromName(selectedPlan.planName)
                                ] || "00.00"
                              } OMR`
                            : "00.00 OMR"}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowConfirm(false)}
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleConfirm}
                  >
                    {t("Confirm")}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </>
  );
}

export default IndividualMedical;
